import React, { useEffect, useState } from "react";
import {
  ExternalLink,
  Mail,
  Trash2,
  User,
  UserPlus,

} from "lucide-react";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { JobFormData } from "@/lib/types";

// Email validator schema
const emailSchema = z.string().email();

interface CollaboratorComponentProps {
  workspaceId: string;
  jobId?: string; // Optional for new jobs
}

interface WorkspaceUser {
  id: string;
  name: string;
  email: string;
  image?: string;
}

const CollaboratorComponent: React.FC<CollaboratorComponentProps> = ({
  workspaceId,
  jobId,
}) => {
  const { setValue, watch, getValues } = useFormContext<JobFormData>();
  const [workspaceUsers, setWorkspaceUsers] = useState<WorkspaceUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCollaborator, setNewCollaborator] = useState({
    email: "",
    name: "",
    role: "reviewer" as "EDITOR" | "REVIEWER" | "APPROVER",
    access: "view" as "VIEW" | "EDIT",
    isExternal: false,
  });
  const [emailValid, setEmailValid] = useState(true);

  // Get collaborators from form values
  const collaborators = watch("basic.collaborators") || [];

  // Fetch workspace users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/v1/workspaces/${workspaceId}/users`);
        if (!response.ok) throw new Error("Failed to fetch workspace users");
        const data = await response.json();
        setWorkspaceUsers(data);
      } catch (error) {
        console.error("Error fetching workspace users:", error);
        toast.error("Failed to fetch workspace users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [workspaceId]);

  // Filter users based on search term
  const filteredUsers = workspaceUsers.filter(
    (user) =>
      user.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Handle selecting a workspace user
  const handleSelectUser = (user: WorkspaceUser) => {
    setNewCollaborator({
      ...newCollaborator,
      email: user.email,
      name: user.name,
      isExternal: false,
    });
    setEmailValid(true);
  };

  // Handle email input change with validation
  const handleEmailChange = (email: string) => {
    setNewCollaborator({
      ...newCollaborator,
      email,
    });

    try {
      emailSchema.parse(email);
      setEmailValid(true);
    } catch (error) {
      setEmailValid(false);
    }
  };

  // Add a new collaborator
  const handleAddCollaborator = () => {
    if (!emailValid || !newCollaborator.email) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Check if collaborator already exists
    const exists = collaborators.some(
      (collab) => collab.email === newCollaborator.email
    );

    if (exists) {
      toast.error("This collaborator has already been added");
      return;
    }

    // Check if user is internal (exists in workspace)
    const internalUser = workspaceUsers.find(
      (user) => user.email === newCollaborator.email
    );

    const collaboratorToAdd = {
      ...newCollaborator,
      userId: internalUser?.id || undefined,
      isExternal: !internalUser,
      name: newCollaborator.name || internalUser?.name || "",
    };

    // Update form values
    const updatedCollaborators = [...collaborators, collaboratorToAdd];
    setValue("basic.collaborators", updatedCollaborators, {
      shouldDirty: true,
    });

    // Reset form
    setNewCollaborator({
      email: "",
      name: "",
      role: "REVIEWER",
      access: "VIEW",
      isExternal: false,
    });
    setShowAddForm(false);
    toast.success(`Collaborator added: ${collaboratorToAdd.email}`);
  };

  // Remove a collaborator
  const handleRemoveCollaborator = (index: number) => {
    const updatedCollaborators = [...collaborators];
    updatedCollaborators.splice(index, 1);
    setValue("basic.collaborators", updatedCollaborators, {
      shouldDirty: true,
    });
    toast.success("Collaborator removed");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-neutral-700">
          Collaborators
        </label>
        {!showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="text-sm flex items-center gap-1 text-neutral-600 hover:text-neutral-900"
          >
            <UserPlus className="w-4 h-4" />
            Add Collaborator
          </button>
        )}
      </div>

      {/* Existing collaborators list */}
      {collaborators.length > 0 ? (
        <div className="border rounded-md divide-y">
          {collaborators.map((collaborator, index) => (
            <div key={index} className="flex items-center justify-between p-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-600">
                  {collaborator.isExternal ? (
                    <ExternalLink className="w-4 h-4" />
                  ) : (
                    <User className="w-4 h-4" />
                  )}
                </div>
                <div>
                  <div className="font-medium text-sm">
                    {collaborator.name || collaborator.email}
                  </div>
                  <div className="text-xs text-neutral-500 flex items-center gap-2">
                    <span>{collaborator.email}</span>
                    <span className="w-1 h-1 rounded-full bg-neutral-300"></span>
                    <span className="capitalize">{collaborator.role}</span>
                    <span className="w-1 h-1 rounded-full bg-neutral-300"></span>
                    <span className="capitalize">
                      {collaborator.access} access
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveCollaborator(index)}
                className="text-neutral-400 hover:text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-neutral-500 italic border rounded-md p-3 bg-neutral-50">
          No collaborators added yet
        </div>
      )}

      {/* Add collaborator form */}
      {showAddForm && (
        <div className="border rounded-md p-4 bg-neutral-50 space-y-4">
          <div className="text-sm font-medium">Add Collaborator</div>

          {/* Email input with search */}
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Email
            </label>
            <div className="relative">
              <input
                type="email"
                value={newCollaborator.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="Enter email address"
                className={`block w-full rounded-md border ${
                  !emailValid ? "border-red-300" : "border-neutral-300"
                } text-sm focus:border-neutral-500 focus:outline-none focus:ring-neutral-500`}
              />
              {!emailValid && newCollaborator.email && (
                <div className="text-xs text-red-500 mt-1">
                  Please enter a valid email address
                </div>
              )}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400">
                <Mail className="w-4 h-4" />
              </div>
            </div>

            {/* Search results */}
            {userSearch && filteredUsers.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-neutral-200 max-h-60 overflow-auto">
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    className="px-3 py-2 hover:bg-neutral-100 cursor-pointer flex items-center gap-2"
                  >
                    {user.image ? (
                      <img
                        src={user.image}
                        alt={user.name}
                        className="w-6 h-6 rounded-full"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-neutral-200 flex items-center justify-center text-xs">
                        {user.name?.[0] || user.email[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium">{user.name}</div>
                      <div className="text-xs text-neutral-500">
                        {user.email}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Name input (for external collaborators) */}
          {!workspaceUsers.some((u) => u.email === newCollaborator.email) &&
            newCollaborator.email && (
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Name (Optional)
                </label>
                <input
                  type="text"
                  value={newCollaborator.name}
                  onChange={(e) =>
                    setNewCollaborator({
                      ...newCollaborator,
                      name: e.target.value,
                    })
                  }
                  placeholder="Enter name"
                  className="block w-full rounded-md border border-neutral-300 text-sm focus:border-neutral-500 focus:outline-none focus:ring-neutral-500"
                />
              </div>
            )}

          {/* Role selection */}
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Role
            </label>
            <select
              value={newCollaborator.role}
              onChange={(e) =>
                setNewCollaborator({
                  ...newCollaborator,
                  role: e.target.value as "EDITOR" | "REVIEWER" | "APPROVER",
                })
              }
              className="block w-full rounded-md border border-neutral-300 text-sm focus:border-neutral-500 focus:outline-none focus:ring-neutral-500"
            >
              <option value="editor">Editor</option>
              <option value="reviewer">Reviewer</option>
              <option value="approver">Approver</option>
            </select>
          </div>

          {/* Access selection */}
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Access Level
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={newCollaborator.access === "VIEW"}
                  onChange={() =>
                    setNewCollaborator({ ...newCollaborator, access: "VIEW" })
                  }
                  className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-500"
                />
                <span className="text-sm">View Only</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={newCollaborator.access === "EDIT"}
                  onChange={() =>
                    setNewCollaborator({ ...newCollaborator, access: "EDIT" })
                  }
                  className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-500"
                />
                <span className="text-sm">Can Edit</span>
              </label>
            </div>
          </div>

          {/* External collaborator note */}
          {newCollaborator.email &&
            !workspaceUsers.some((u) => u.email === newCollaborator.email) && (
              <div className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2 text-yellow-800">
                This email is not associated with any workspace user. An invite
                will be sent to this external collaborator.
              </div>
            )}

          {/* Action buttons */}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddCollaborator}
              disabled={!emailValid || !newCollaborator.email}
              className="px-3 py-1.5 text-sm bg-neutral-900 text-white rounded-md hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <UserPlus className="w-3 h-3" />
              Add Collaborator
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(CollaboratorComponent);
